import { useQuery } from 'react-query';
import { authFetch } from '../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';

export enum MortgageType {
  Fixed30Y,
  Fixed15Y,
  Arm10Y,
  Arm5Y,
  Arm1Y,
  InterestOnly,
  Fixed10Y,
  Fixed20Y,
  Fixed25Y,
  Fixed40Y,
}

export enum GoalType {
  Retirement = 'retirement',
  Education = 'education',
  EmergencyFund = 'emergency_fund',
  HealthCare = 'health_care',
  Insurance = 'insurance',
  DebtManagement = 'debt_management',
  HomePurchase = 'home_purchase',
  EstatePlanning = 'estate_planning',
  LargePurchase = 'large_purchase',
  Decumulation = 'decumulation',
}

export interface GoalDTO<T = any> {
  id: string | null;
  type: GoalType;
  name: string;
  isSelected: boolean;
  isDisabled: boolean;
  disabledMessage?: string;
  goalData: T; // each goal has its own data model
  goalOrigin?: 'PulseCheck' | null;
  assetBucketingIndices: number[];
}

interface EstateOptionsDTO {
  lifetimeGiftingExemptionAmount: number;
  baseLifetimeGiftingExemptionAmount: number;
}

export interface EstatePlanningOptionsDTO {
  primaryEstateOptions: EstateOptionsDTO;
  spouseEstateOptions?: EstateOptionsDTO;
  statesWithEstateTax: number[];
  addUnusedExemption: boolean;
}

export interface FillingStateOptionsDTO {
  effectiveTaxRate: number;
  exclusionAmount: number;
  isInsideUS: boolean;
  label: string;
  value: number;
}

export interface GoalsDTO<T = any> {
  goals: GoalDTO<T>[];
  mortgageTypeOptions: { label: string; value: MortgageType }[];
  yearsOptions: number[];
  assetBucketingOptions: {
    enableGoalAssetBucketing: boolean;
    availableBuckets: Record<number, string>;
  };
  estatePlanningOptions: EstatePlanningOptionsDTO;
  filingStateOptions: FillingStateOptionsDTO[];
}

interface FetchParams {
  clientId: string;
}

export const fetchGoals = async <T = any>({ clientId }: FetchParams): Promise<GoalsDTO<T>> => {
  return authFetch(urlWithParams('/Consumer/GoalSelectionStep', { clientId }));
};

interface QueryParams {
  clientId: string;
}

export const useGoalsQuery = <T = any>({ clientId }: QueryParams) => {
  return useQuery<GoalsDTO<T>>(
    queryKeys.goals(clientId),

    () => fetchGoals<T>({ clientId }),

    {
      cacheTime: 0,
    },
  );
};
